import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { NumberInput, RegularModal } from '@/components_deprecated'
import { DetectBlur } from '@/containers/util-containers'
import { Field } from '@/modules/companies'
import styled, { css } from 'styled-components'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'

const ButtonFlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`

const PartOfDayButton = styled.div<{ selected: boolean }>`
  ${props =>
    props.selected &&
    css`
      color: white;
      background: ${props.theme.colors.primary};

      &:first-child {
        background: gray;
      }
      &:hover {
        cursor: default;
      }
    `}

  border: 1px solid ${props => props.theme.colors.gray6};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  padding: 1rem;
  cursor: pointer;
  min-width: 50px;
  text-align: center;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;

  & > div + div {
    margin-top: 1rem;
  }
`

interface PartOfDayButtonProps {
  value: number
  text: string
  selected: boolean
  onClick: (value: number, event?: MouseEvent | undefined) => void
}

const PartOfDay: React.JSX.CFC<PartOfDayButtonProps> = ({
  value,
  text,
  selected,
  onClick,
}) => {
  return (
    <PartOfDayButton selected={selected} onClick={() => onClick(value)}>
      {text}
    </PartOfDayButton>
  )
}

export interface SalaryDayForm {
  userId: string
  partOfDay: number
  salaryCodeId: string | null
}

interface SalaryDayPromptProps {
  initialData: SalaryDayForm
  /** Above 1 for a rest, which is worth one day per night it covers. */
  maxPartOfDay?: number
  onSubmit: PromptResolve<SalaryDayForm | null>
}

const BUTTON_PROPS = [
  { value: 0, text: '0' },
  { value: 0.25, text: '1/4' },
  { value: 0.333, text: '1/3' },
  { value: 0.5, text: '1/2' },
  { value: 0.666, text: '2/3' },
  { value: 0.75, text: '3/4' },
  { value: 1, text: '1' },
]

export const SalaryDayPrompt: React.FC<SalaryDayPromptProps> = ({
  initialData,
  maxPartOfDay = 1,
  onSubmit,
}) => {
  const translations = useTranslate({
    update: 'common.update',
    addSalaryDay: 'activities.add-salary-day',
    required: 'common.required',
    salaryCode: 'common.salary-code',
    partOfDay: 'activities.part-of-day',
  })

  const {
    formValues: form,
    formValid,
    formChangeHandler: handler,
    submitHandler: submit,
    formErrors,
  } = useForm<SalaryDayForm>({
    values: initialData ?? {
      userId: '',
      partOfDay: 1,
      salaryCodeId: '',
    },
    validators: {
      salaryCodeId: value => (value !== null ? null : translations.required),
    },
  })

  // Whole days past 1 for a rest, which is worth one per night it covers.
  const partOfDayButtons = [
    ...BUTTON_PROPS,
    ...Array.from(
      { length: Math.max(0, Math.floor(maxPartOfDay) - 1) },
      (_, i) => ({ value: i + 2, text: String(i + 2) })
    ),
  ]

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={translations.addSalaryDay}
        submitText={translations.update}
        submitDisabled={!formValid}
        width="500px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <Form>
          <Field>
            <label>{translations.salaryCode}</label>
            <SalaryCodeSelect
              value={form.salaryCodeId}
              error={formErrors.salaryCodeId}
              isSalaryDay={true}
              width="100%"
              setOnLoad={true}
              onChange={handler('salaryCodeId')}
            />
          </Field>

          <Field>
            <label>{translations.partOfDay}</label>
            <NumberInput
              max={maxPartOfDay}
              min={0}
              step={maxPartOfDay > 1 ? 1 : 0.25}
              value={form.partOfDay}
              onChange={handler('partOfDay')}
            />

            <ButtonFlexContainer>
              {partOfDayButtons.map(props => (
                <PartOfDay
                  key={props.value}
                  selected={form.partOfDay === props.value}
                  {...props}
                  onClick={handler('partOfDay')}
                />
              ))}
            </ButtonFlexContainer>
          </Field>
        </Form>
      </RegularModal>
    </DetectBlur>
  )
}
